import { get } from 'lodash';
import React from 'react';
import { createTeleporter } from 'react-teleporter';

import { observer } from 'mobx-react-lite';
import { FormikInput, FormikSliderFieldSet, FormikCombobox, FormikMultiCombobox } from '@src/features/common';
import { useStore } from '@src/models';
import { months, years } from 'src/constants';
import { BaseBox, Flex, Text, ErrorMessage } from 'src/kit';

const SelectValues = createTeleporter();

export const Experience = React.memo(({ resourcesStore, index, experienceValues, errors, setFieldValue }: any) => {
  const destroyFocuses = () => {
    setFieldValue(`experiences_attributes.${index}.skills_attributes`, []);
  };

  return (
    <BaseBox mr={20} flex="0 1 calc(100% - 120px)">
      <Flex mb={20}>
        <FormikInput isFast name={`experiences_attributes.${index}.position_title`} placeholder="Title" size="md" />
        <Text mx={10} color="blueGreyTwo" fontSize={12} lineHeight="40px">
          at
        </Text>
        <FormikInput isFast name={`experiences_attributes.${index}.title`} placeholder="Company" size="md" />
      </Flex>
      <Flex mb={20}>
        <FormikMultiCombobox
          isFast
          classicAppearance
          name={`experiences_attributes.${index}.industries`}
          placeholder="Industries"
          items={resourcesStore.industries}
          mr={30}
          flex="1 1 0%"
        />
        <FormikMultiCombobox
          isFast
          classicAppearance
          name={`experiences_attributes.${index}.job_functions`}
          placeholder="Job functions"
          items={resourcesStore.job.functions}
          flex="1 1 0%"
        />
      </Flex>
      <Flex mb={20} alignItems="baseline">
        <FormikCombobox
          isFast
          name={`experiences_attributes.${index}.startDate.month`}
          items={months}
          placeholder="Month"
          mr={15}
          width={126}
        />
        <FormikCombobox
          isFast
          name={`experiences_attributes.${index}.startDate.year`}
          items={years}
          placeholder="Year"
          width={100}
        />
        <Text mx={10} color="blueGreyTwo" fontSize={12} lineHeight="40px">
          to
        </Text>
        <div>
          <Flex>
            <FormikCombobox
              isFast
              name={`experiences_attributes.${index}.endDate.month`}
              items={['Current', ...months]}
              placeholder="Month"
              mr={15}
              width={126}
            />
            {experienceValues.endDate?.month !== 'Current' && (
              <FormikCombobox
                isFast
                name={`experiences_attributes.${index}.endDate.year`}
                items={years}
                placeholder="Year"
                width={100}
              />
            )}
          </Flex>
          <ErrorMessage mt={0}>{get(errors, `experiences_attributes.${index}.endDate.global`)}</ErrorMessage>
        </div>
      </Flex>
      <Flex mb={20}>
        <MainThemes
          mainThemes={resourcesStore.themesById.main_themes}
          baseName={`experiences_attributes.${index}`}
          setFieldValue={setFieldValue}
          destroyFocuses={destroyFocuses}
        />
        <SubThemes
          subThemes={resourcesStore.themesById.sub_themes}
          baseName={`experiences_attributes.${index}`}
          experienceValues={experienceValues}
          setFieldValue={setFieldValue}
          destroyFocuses={destroyFocuses}
        />
      </Flex>
      <Focuses
        allThemes={resourcesStore.themesById.allThemes}
        baseName={`experiences_attributes.${index}`}
        experienceValues={experienceValues}
        setFieldValue={setFieldValue}
      />
    </BaseBox>
  );
});

const MainThemes = ({ baseName, mainThemes, setFieldValue, destroyFocuses }) => {
  return (
    <FormikCombobox
      isFast
      name={`${baseName}.main_theme`}
      items={mainThemes}
      shape={{ labelKey: 'name', valueKey: 'theme_id' }}
      onChange={() => {
        setFieldValue(`${baseName}.sub_theme`, null);
        destroyFocuses();
      }}
      label="Main area:"
      placeholder="Select an option"
      mr={30}
      flex="0 1 calc(50% - 15px)"
    />
  );
};

const SubThemes = observer(({ baseName, experienceValues, destroyFocuses }) => {
  const main_theme = experienceValues.main_theme;
  const { resourcesStore } = useStore();
  const subThemes = resourcesStore.themesById.sub_themes;
  if (!main_theme) return null;

  const subThemesOfMainTheme = main_theme.children
    .reduce((acc, id) => {
      const subTheme = subThemes.find(subTheme => subTheme.theme_id === id);
      if (subTheme) {
        acc.push(subTheme);
      }
      return acc;
    }, [])
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <FormikCombobox
      isFast={false}
      name={`${baseName}.sub_theme`}
      items={subThemesOfMainTheme}
      shape={{ labelKey: 'name', valueKey: 'theme_id' }}
      label="Activity:"
      ignoreCached
      placeholder="Select an option"
      onChange={() => {
        destroyFocuses();
      }}
      flex="0 1 calc(50% - 15px)"
    />
  );
});

const Focuses = ({ baseName, experienceValues, allThemes, setFieldValue }) => {
  const { resourcesStore } = useStore();

  const Wrapper = React.useRef(({ children }) => <MultiValuesWrapper {...{ children, baseName }} />).current;

  const { main_theme, sub_theme } = experienceValues;
  if (!main_theme || !sub_theme) return null;
  const subThemesChildrenIds = [...(sub_theme.children ? sub_theme.children : [])];
  const skills = [...resourcesStore.themesById.skills_families, ...resourcesStore.themesById.focuses]
    .filter(({ theme_id }) => subThemesChildrenIds.includes(theme_id))
    .filter(Boolean);
  const currentValue = experienceValues.skills_attributes;
  const selectValue = currentValue.filter(item => !item._destroy);

  const focuses = skills.filter(focus => focus.level === 'focus');
  const families = skills
    .filter(focus => focus.level === 'skills_family')
    .map(family => ({
      ...family,
      children: allThemes[family.theme_id].children.map(focusId => allThemes[focusId]).filter(Boolean)
    }));
  const focusesOptions = focuses;
  const groupedOptions = families.map(family => ({
    name: family.name,
    items: family.children
  }));
  const allOptions = [].concat(focusesOptions).concat(groupedOptions);

  if (!allOptions.length) return null;

  return (
    <>
      <FormikMultiCombobox
        isFast
        multiValuesWrapper={Wrapper}
        name={`${baseName}.skills_attributes`}
        label="Expertise:"
        placeholder="Select an option"
        shape={{ labelKey: 'name', valueKey: 'theme_id' }}
        value={selectValue}
        items={allOptions}
        onSelect={selectedItem => {
          const selectedItemIndex = currentValue.findIndex(item => item.theme_id === selectedItem.theme_id);
          if (selectedItemIndex !== -1) {
            setFieldValue(`${baseName}.skills_attributes.${selectedItemIndex}`, {
              ...currentValue[selectedItemIndex],
              _destroy: false
            });
          } else {
            setFieldValue(`${baseName}.skills_attributes`, [...currentValue, selectedItem]);
          }
        }}
        onRemove={selectedItem => {
          const removedItemIndex = currentValue.findIndex(item => item.theme_id === selectedItem.theme_id);
          if (selectedItem.id) {
            setFieldValue(`${baseName}.skills_attributes.${removedItemIndex}`, {
              ...currentValue[removedItemIndex],
              _destroy: true
            });
          } else {
            setFieldValue(
              `${baseName}.skills_attributes`,
              currentValue.filter((item, index) => index !== removedItemIndex)
            );
          }
        }}
      />
      <SelectValues.Target />
    </>
  );
};

const MultiValuesWrapper = ({ children, baseName }) => (
  <SelectValues.Source>
    <BaseBox mt={15}>
      {React.Children.map(children, (child, index) => (
        <Flex flex={1} alignItems="center" justifyContent="space-between">
          {React.cloneElement(child, { maxWidth: '50%' })}
          <FormikSliderFieldSet
            name={`${baseName}.skills_attributes.${index}.experience`}
            sliderProps={{ label: 'Skill level' }}
            mb={10}
            width="calc(50% - 15px)"
          />
        </Flex>
      ))}
    </BaseBox>
  </SelectValues.Source>
);
